import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LogIn,
  Building2,
  User,
  Bed,
  Home,
  Download,
  Edit,
  Upload,
  Trash2,
  Database,
  RefreshCw,
  UserMinus,
  Users
} from 'lucide-react';
import { AccommodationTable as TableType } from '../../types/accommodation';
import { useAccommodationStore } from '../../stores/accommodationStore';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { useConfirm } from '../../hooks/useConfirm';
import { useLanguage } from '../../contexts/LanguageContext';
import toast from 'react-hot-toast';
import { RoomBulkEditModal } from './RoomBulkEditModal';
import { BulkAccommodationModal } from './BulkAccommodationModal';
import { doc, getDoc, collection, query, where, getDocs, writeBatch } from 'firebase/firestore';
import { firestore } from '../../lib/firebase/config';
import { exportAccommodationToExcel } from '../../utils/exportAccommodation';
import { ExcludeOrganizersModal } from './ExcludeOrganizersModal';
import { EasyBulkAccommodationModal } from './EasyBulkAccommodationModal';
import { ExcludePackagesModal } from './ExcludePackagesModal';
import { RemovePilgrimsModal } from './RemovePilgrimsModal';

type Props = {
  table: TableType;
};

export const AccommodationTable = ({ table }: Props) => {
  const navigate = useNavigate();
  const { translations } = useLanguage();
  const { deleteTable, removeAllAssignments } = useAccommodationStore();
  const { isOpen, config, confirm, close } = useConfirm();
  const userRole = localStorage.getItem('userRole');
  const userId = localStorage.getItem('userId');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isBulkAccommodationOpen, setIsBulkAccommodationOpen] = useState(false);
  const [isEasyBulkAccommodationOpen, setIsEasyBulkAccommodationOpen] = useState(false);
  const [documentSize, setDocumentSize] = useState<number | null>(null);
  const [isRemoveAllOpen, setIsRemoveAllOpen] = useState(false);
  const [isExcludeOrganizersOpen, setIsExcludeOrganizersOpen] = useState(false);
  const [isOrganizerRemoveOpen, setIsOrganizerRemoveOpen] = useState(false);
  const [isExcludePackagesOpen, setIsExcludePackagesOpen] = useState(false);
  const [isRemovePilgrimsOpen, setIsRemovePilgrimsOpen] = useState(false);
  const [isRemovingAll, setIsRemovingAll] = useState(false);

  // Calculate statistics
  const totalRooms = table.r.length;
  const occupiedRooms = table.r.filter(
    (room) => room.o > 0
  ).length;
  const totalCapacity = table.r.reduce(
    (total, room) => total + room.c,
    0
  );
  const totalOccupied = table.r.reduce(
    (total, room) => total + room.o,
    0
  );
  const occupancyRate = Math.round((totalOccupied / totalCapacity) * 100);

  // Calculate gender statistics - Count all pilgrims across all active assignments
  // This now aligns with how room.o (total occupied) is calculated
  const genderStats = table.r.reduce(
    (stats, room) => {
      room.b.forEach(bed => {
        // Count all assignments in each bed
        if (bed.a && bed.a.length > 0) {
          bed.a.forEach(assignment => {
            if (assignment.g === 'M') {
              stats.male++;
            } else if (assignment.g === 'F') {
              stats.female++;
            }
          });
        }
      });
      return stats;
    },
    { male: 0, female: 0 }
  );

  const totalAssigned = genderStats.male + genderStats.female;
  const malePercentage = totalAssigned ? Math.round((genderStats.male / totalAssigned) * 100) : 0;
  const femalePercentage = totalAssigned ? Math.round((genderStats.female / totalAssigned) * 100) : 0;

  // Fetch document size for admin
  useEffect(() => {
    if (userRole === 'admin') {
      const fetchDocumentSize = async () => {
        try {
          const docRef = doc(firestore, 'AccommodationTables', table.i);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            // Calculate size in KB
            const docSize = new TextEncoder().encode(JSON.stringify(docSnap.data())).length / 1024;
            setDocumentSize(Number(docSize.toFixed(2)));
          }
        } catch (error) {
          console.error('Error fetching document size:', error);
        }
      };

      fetchDocumentSize();
    }
  }, [table.i, userRole]);

  const handleExport = () => {
    try {
      // Filter rooms based on user role
      const filteredRooms = table.r.map(room => ({
        ...room,
        b: room.b.map(bed => ({
          ...bed,
          a: bed.a?.filter(assignment => {
            if (userRole === 'admin') return true;
            if (userRole === 'guide') return assignment.gid === userId;
            if (userRole === 'organizer') return assignment.oid === userId;
            return false;
          })
        }))
      }));

      const filteredTable = {
        ...table,
        r: filteredRooms
      };

      exportAccommodationToExcel(filteredTable);
      toast.success(
        translations?.messages?.exportSuccess ||
          'Accommodation data exported successfully'
      );
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error(
        translations?.messages?.exportError ||
          'Failed to export accommodation data'
      );
    }
  };

  const handleDelete = async () => {
    try {
      await deleteTable(table.i);
      toast.success(
        translations?.messages?.deleteSuccess ||
          'Accommodation table deleted successfully'
      );
    } catch (error: any) {
      console.error('Error deleting table:', error);
      toast.error(
        translations?.messages?.deleteError ||
          'Failed to delete accommodation table'
      );
    }
    setIsRemoveAllOpen(false);
  };

  const confirmDelete = () => {
    confirm({
      title:
        translations?.confirmDialog?.deleteTitle ||
        'Delete Accommodation Table',
      message: (
        translations?.confirmDialog?.deleteMessage ||
        'Are you sure you want to delete the table "{name}"?'
      ).replace('{name}', table.tn),
      onConfirm: handleDelete,
      type: 'danger',
    });
  };

  const handleRemoveAllAssignmentsClick = () => {
    if (userRole === 'admin') {
      setIsExcludeOrganizersOpen(true);
    } else {
      setIsRemoveAllOpen(true);
    }
  };

  const handleOrganizerRemoveAssignmentsClick = () => {
    // For organizers, open the package selection modal
    setIsExcludePackagesOpen(true);
  };

  const handleRemoveAllAssignments = async (excludedOrganizerIds: string[] = [], excludedCountries: string[] = [], excelFile?: File) => {
    try {
      setIsRemovingAll(true);
      await removeAllAssignments(table.i, excludedOrganizerIds, excludedCountries, excelFile);
      toast.success(
        translations?.messages?.removeAllSuccess ||
          'All assignments removed successfully'
      );
      setIsRemoveAllOpen(false);
      setIsExcludeOrganizersOpen(false);
      setIsOrganizerRemoveOpen(false);
      setIsRemovePilgrimsOpen(false);
    } catch (error: any) {
      console.error('Error removing all assignments:', error);
      toast.error(
        translations?.messages?.removeAllError ||
          'Failed to remove all assignments'
      );
    } finally {
      setIsRemovingAll(false);
    }
  };

  const handleOrganizerRemoveAssignments = async () => {
    try {
      if (!userId) {
        throw new Error('User ID not found');
      }
      
      setIsRemovingAll(true);
      
      // For organizer, we only want to remove their own assignments
      // So we exclude all other organizers by passing an empty array
      // The removeAllAssignments function will only remove assignments
      // from the current organizer when no exclusions are specified for an organizer
      await removeAllAssignments(table.i, []);
      
      toast.success(
        translations?.messages?.removeMyAssignmentsSuccess ||
          'Your assignments removed successfully'
      );
      
      setIsOrganizerRemoveOpen(false);
    } catch (error: any) {
      console.error('Error removing organizer assignments:', error);
      toast.error(
        translations?.messages?.removeAllError ||
          'Failed to remove all assignments'
      );
    } finally {
      setIsRemovingAll(false);
    }
  };

  const handleExcludeOrganizersConfirm = (excludedOrganizerIds: string[], excludedCountries: string[], excelFile?: File) => {
    if (excelFile) {
      // If an Excel file is provided, process it
      handleRemoveAllAssignments([], [], excelFile);
    } else if (excludedOrganizerIds.length > 0 || excludedCountries.length > 0) {
      // Proceed with removal, excluding selected organizers and/or countries
      handleRemoveAllAssignments(excludedOrganizerIds, excludedCountries);
    } else {
      // If nothing is excluded, show the regular confirmation dialog
      setIsExcludeOrganizersOpen(false);
      setIsRemoveAllOpen(true);
    }
  };

  const handleExcludePackagesConfirm = async (excludedPackageIds: string[]) => {
    try {
      setIsRemovingAll(true);
      
      // Get the selectors for the current organizer
      const selectorsRef = collection(firestore, 'PilgrimSelector');
      const selectorQuery = query(
        selectorsRef, 
        where('organizerId', '==', userId),
        where('hotels', 'array-contains', table.hn)
      );
      
      const snapshot = await getDocs(selectorQuery);
      
      // Build a list of pilgrim IDs to exclude based on their package
      const pilgrimIdsToExclude = snapshot.docs
        .filter(doc => excludedPackageIds.includes(doc.data().packageData.packageName))
        .map(doc => doc.id);
      
      // Remove assignments except for excluded pilgrim IDs
      const batch = writeBatch(firestore);
      
      // Create a copy of the rooms array to modify
      const updatedRooms = JSON.parse(JSON.stringify(table.r));
      
      // Process each room
      let assignmentsRemoved = 0;
      
      updatedRooms.forEach((room: any) => {
        let roomOccupancyChange = 0;
        
        // Process each bed in the room
        room.b.forEach((bed: any) => {
          if (bed.a && bed.a.length > 0) {
            // Count how many assignments will be removed
            const assignmentCountBefore = bed.a.length;
            
            // Keep assignments for excluded pilgrim IDs and non-organizer assignments
            bed.a = bed.a.filter((assignment: any) => {
              // Keep if not by this organizer
              if (assignment.oid !== userId) return true;
              
              // Keep if pilgrim ID is in the excluded list
              return pilgrimIdsToExclude.includes(assignment.pid);
            });
            
            // Count removed assignments
            const assignmentCountAfter = bed.a.length;
            const removedFromBed = assignmentCountBefore - assignmentCountAfter;
            
            assignmentsRemoved += removedFromBed;
            roomOccupancyChange += removedFromBed;
          }
        });
        
        // Update room occupancy
        room.o = Math.max(0, room.o - roomOccupancyChange);
      });
      
      // Update table with modified rooms
      const tableRef = doc(firestore, 'AccommodationTables', table.i);
      batch.update(tableRef, {
        r: updatedRooms,
        ua: Date.now()
      });
      
      // Update PilgrimSelector documents for affected pilgrims
      snapshot.docs.forEach(doc => {
        const pilgrimData = doc.data();
        
        // Skip pilgrims in excluded packages
        if (excludedPackageIds.includes(pilgrimData.packageData.packageName)) {
          return;
        }
        
        // Update accommodation status
        const updatedAccommodation = { ...pilgrimData.isAccommodated } || {};
        updatedAccommodation[table.hn] = false;
        
        batch.update(doc.ref, {
          isAccommodated: updatedAccommodation,
          updatedAt: Date.now()
        });
      });
      
      // Commit all changes
      await batch.commit();
      
      toast.success(`Successfully removed ${assignmentsRemoved} pilgrim assignments, excluding selected packages`);
      
    } catch (error: any) {
      console.error('Error removing package-specific assignments:', error);
      toast.error(error.message || 'Failed to remove assignments');
    } finally {
      setIsRemovingAll(false);
      setIsExcludePackagesOpen(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="p-4 md:p-6 border-b">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 md:p-3 bg-primary-50 rounded-xl">
              <Building2 className="text-primary-500" size={20} md:size={24} />
            </div>
            <div>
              <h3 className="text-base md:text-lg font-semibold text-gray-900">
                {table.tn}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                <Home size={14} md:size={16} className="text-gray-400" />
                <p className="text-xs md:text-sm text-gray-500">
                  {table.hn}
                </p>
              </div>
              {userRole === 'admin' && documentSize !== null && (
                <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                  <Database size={14} className="text-gray-400" />
                  <span>
                    {documentSize} KB / 1024 KB
                    {documentSize > 900 && (
                      <span className="text-red-500 ml-1">(Warning: Approaching limit)</span>
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Statistics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mt-4 md:mt-6">
          {/* Rooms Stats */}
          <div className="bg-gray-50 p-3 md:p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2 md:mb-3">
              <Building2 size={16} md:size={18} className="text-primary-500" />
              <span className="text-xs md:text-sm text-gray-600">
                {translations?.statistics?.rooms || 'Rooms'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs md:text-sm text-gray-500">
                {translations?.statistics?.occupiedTotal || 'Occupied / Total'}
              </span>
              <span className="text-base md:text-lg font-semibold">
                {occupiedRooms} / {totalRooms}
              </span>
            </div>
            <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5 md:h-2">
              <div
                className="h-full rounded-full bg-primary-500"
                style={{ width: `${Math.min((occupiedRooms / totalRooms) * 100, 100)}%` }}
              />
            </div>
          </div>

          {/* Beds Stats */}
          <div className="bg-gray-50 p-3 md:p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2 md:mb-3">
              <Bed size={16} md:size={18} className="text-primary-500" />
              <span className="text-xs md:text-sm text-gray-600">
                {translations?.statistics?.beds || 'Beds'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs md:text-sm text-gray-500">
                {translations?.statistics?.occupiedCapacity || 'Occupied / Capacity'}
              </span>
              <span className="text-base md:text-lg font-semibold">
                {totalOccupied} / {totalCapacity}
              </span>
            </div>
            <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5 md:h-2">
              <div
                className={`h-full rounded-full transition-all ${
                  occupancyRate > 90
                    ? 'bg-red-500'
                    : occupancyRate > 70
                    ? 'bg-yellow-500'
                    : 'bg-green-500'
                }`}
                style={{ width: `${Math.min(occupancyRate, 100)}%` }}
              />
            </div>
          </div>

          {/* Gender Distribution */}
          <div className="bg-gray-50 p-3 md:p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2 md:mb-3">
              <User size={16} md:size={18} className="text-primary-500" />
              <span className="text-xs md:text-sm text-gray-600">
                {translations?.statistics?.genderDistribution || 'Gender Distribution'}
              </span>
            </div>
            <div className="space-y-2">
              {/* Male Stats */}
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500" />
                    <span className="text-sm text-gray-600">
                      {translations?.statistics?.male || 'Male'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{genderStats.male}</span>
                    <span className="text-xs text-gray-500">({malePercentage}%)</span>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                  <div
                    className="h-full bg-blue-500 transition-all"
                    style={{ width: `${malePercentage}%` }}
                  />
                </div>
              </div>

              {/* Female Stats */}
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-pink-500" />
                    <span className="text-sm text-gray-600">
                      {translations?.statistics?.female || 'Female'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{genderStats.female}</span>
                    <span className="text-xs text-gray-500">({femalePercentage}%)</span>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                  <div
                    className="h-full bg-pink-500 transition-all"
                    style={{ width: `${femalePercentage}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="px-4 md:px-6 py-3 md:py-4 bg-gray-50 flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex flex-wrap items-stretch md:items-center gap-3 w-full sm:w-auto">
          <button
            onClick={() => navigate(`/accommodation/${table.i}`)}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
          >
            <LogIn size={18} />
            <span>{translations?.actions?.viewDetails || 'View Details'}</span>
          </button>

          {/* Export Button - Available for all roles */}
          <button
            onClick={handleExport}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors w-full sm:w-auto"
            title={
              translations?.actions?.download || 'Download Accommodation'
            }
          >
            <Download size={18} />
            <span>
              {translations?.actions?.download || 'Download Accommodation'}
            </span>
          </button>

          {/* Easy Bulk Accommodation Button - For admin and organizer */}
          {(userRole === 'admin' || userRole === 'organizer') && (
            <button
              onClick={() => setIsEasyBulkAccommodationOpen(true)}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors w-full sm:w-auto"
              title={translations?.actions?.easyBulkAccommodation || 'Easy Bulk Accommodation'}
            >
              <Users size={18} />
              <span>
                {translations?.actions?.easyBulkAccommodation || 'Easy Bulk Accommodation'}
              </span>
            </button>
          )}

          {/* Remove Assignments by Package - For organizers */}
          {userRole === 'organizer' && (
            <button
              onClick={handleOrganizerRemoveAssignmentsClick}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-orange-50 text-orange-600 rounded-lg hover:bg-orange-100 transition-colors w-full sm:w-auto"
              title={translations?.actions?.removeByPackage || 'Remove by Package'}
            >
              <UserMinus size={18} />
              <span>
                {translations?.actions?.removeByPackage || 'Remove by Package'}
              </span>
            </button>
          )}

          {/* Remove Specific Pilgrims - For admin and organizer */}
          {(userRole === 'admin' || userRole === 'organizer') && (
            <button
              onClick={() => setIsRemovePilgrimsOpen(true)}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition-colors w-full sm:w-auto"
              title={translations?.actions?.removeSpecificPilgrims || 'Remove Specific Pilgrims'}
            >
              <Upload size={18} />
              <span>
                {translations?.actions?.removeSpecificPilgrims || 'Remove Specific Pilgrims'}
              </span>
            </button>
          )}

          {/* Edit Button - Only for admin */}
          {userRole === 'admin' && (
            <>
              <button
                onClick={() => setIsEditModalOpen(true)}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors w-full sm:w-auto"
                title={translations?.actions?.editRoom || 'Edit Rooms'}
              >
                <Edit size={18} />
                <span>
                  {translations?.actions?.editRoom || 'Edit Rooms'}
                </span>
              </button>

              <button
                onClick={() => setIsBulkAccommodationOpen(true)}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors w-full sm:w-auto"
                title={translations?.actions?.bulkAccommodation || 'Bulk Accommodation'}
              >
                <Upload size={18} />
                <span>
                  {translations?.actions?.bulkAccommodation || 'Bulk Accommodation'}
                </span>
              </button>

              {/* Remove All Assignments Button */}
              <button
                onClick={handleRemoveAllAssignmentsClick}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-orange-50 text-orange-600 rounded-lg hover:bg-orange-100 transition-colors w-full sm:w-auto"
                title={translations?.actions?.removeAllAssignments || 'Remove All Assignments'}
              >
                <UserMinus size={18} />
                <span>
                  {translations?.actions?.removeAllAssignments || 'Remove All Assignments'}
                </span>
              </button>

              {/* Delete Button */}
              <button
                onClick={confirmDelete}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors w-full sm:w-auto"
                title={translations?.actions?.delete || 'Delete Table'}
              >
                <Trash2 size={18} />
                <span>
                  {translations?.actions?.delete || 'Delete Table'}
                </span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Confirm Dialog */}
      {isOpen && config && (
        <ConfirmDialog
          isOpen={isOpen}
          onClose={close}
          onConfirm={config.onConfirm}
          title={config.title}
          message={config.message}
          type={config.type}
          confirmText={translations?.confirmDialog?.delete || 'Delete'}
          cancelText={translations?.confirmDialog?.cancel || 'Cancel'}
        />
      )}

      {/* Remove All Assignments Confirm Dialog */}
      <ConfirmDialog
        isOpen={isRemoveAllOpen}
        onClose={() => setIsRemoveAllOpen(false)}
        onConfirm={() => handleRemoveAllAssignments([], [])}
        title={translations?.confirmDialog?.removeAllAssignmentsTitle || 'Remove All Assignments'}
        message={translations?.confirmDialog?.removeAllAssignmentsMessage || 
          'Are you sure you want to remove ALL pilgrim assignments from this table? This will remove all pilgrims from all rooms.'}
        type="danger"
        confirmText={isRemovingAll ? 
          (translations?.confirmDialog?.removing || 'Removing...') : 
          (translations?.confirmDialog?.removeAll || 'Remove All')}
        cancelText={translations?.confirmDialog?.cancel || 'Cancel'}
      />

      {/* Organizer Remove Assignments Confirm Dialog */}
      <ConfirmDialog
        isOpen={isOrganizerRemoveOpen}
        onClose={() => setIsOrganizerRemoveOpen(false)}
        onConfirm={handleOrganizerRemoveAssignments}
        title={translations?.confirmDialog?.removeMyAssignmentsTitle || 'Remove My Assignments'}
        message={translations?.confirmDialog?.removeMyAssignmentsMessage || 
          'Are you sure you want to remove all YOUR pilgrim assignments from this hotel? This will only remove pilgrims assigned by you.'}
        type="danger"
        confirmText={isRemovingAll ? 
          (translations?.confirmDialog?.removing || 'Removing...') : 
          (translations?.confirmDialog?.removeMyAssignments || 'Remove My Assignments')}
        cancelText={translations?.confirmDialog?.cancel || 'Cancel'}
      />

      {/* Exclude Organizers Modal */}
      <ExcludeOrganizersModal
        isOpen={isExcludeOrganizersOpen}
        onClose={() => setIsExcludeOrganizersOpen(false)}
        onConfirm={handleExcludeOrganizersConfirm}
      />

      {/* Exclude Packages Modal - New! */}
      <ExcludePackagesModal
        isOpen={isExcludePackagesOpen}
        onClose={() => setIsExcludePackagesOpen(false)}
        onConfirm={handleExcludePackagesConfirm}
        tableId={table.i}
      />

      {/* Easy Bulk Accommodation Modal */}
      <EasyBulkAccommodationModal
        isOpen={isEasyBulkAccommodationOpen}
        onClose={() => setIsEasyBulkAccommodationOpen(false)}
        tableId={table.i}
      />

      {/* Remove Pilgrims Modal */}
      {isRemovePilgrimsOpen && (
        <RemovePilgrimsModal
          isOpen={true}
          onClose={() => setIsRemovePilgrimsOpen(false)}
          tableId={table.i}
          hotelName={table.hn}
        />
      )}

      {isEditModalOpen && (
        <RoomBulkEditModal
          isOpen={true}
          onClose={() => setIsEditModalOpen(false)}
          tableId={table.i}
          currentRooms={table.r}
        />
      )}

      {isBulkAccommodationOpen && (
        <BulkAccommodationModal
          isOpen={true}
          onClose={() => setIsBulkAccommodationOpen(false)}
          tableId={table.i}
        />
      )}
    </div>
  );
};